// FAT-44: painel da candidata do Alpha — candles SEMANAIS + RSI(2) semanal (lightweight-charts, Apache 2.0, atribuição TradingView).
// Candles e RSI vêm prontos do backend (GET /api/alpha/grafico/:ticker, mesmo cálculo do scanner); nada é recalculado aqui.
// O cabeçalho usa a linha já carregada na tabela.
import React, { useEffect, useRef, useState } from 'react';
import { createChart, CrosshairMode, LineStyle } from 'lightweight-charts';
import './AlphaGraficoPainel.css';
import { getAlphaGrafico } from '../api/brapi';
import { motivoAlpha, percentualAlpha } from '../utils/csvCandidatasAlpha';

const ALTA = '#26a69a';
const BAIXA = '#ef5350';
const DESTAQUE = '#f5c542';
const ENTRADA = '#4fc3f7';
const SAIDA = '#ce93d8';
// FAT-47: escala do RSI fixa (sem margem: o eixo não passa de 100 nem fica abaixo de 0, independente dos dados/marcadores).
export const ESCALA_RSI = Object.freeze({ minValue: 0, maxValue: 100 });
export const NOTA_POSICAO = 'avaliada no fechamento diário; não garante execução nesse preço';
const DISCRETO = 'rgba(155,163,184,0.75)';
const FUNDO = '#0f1a2e';
const TEXTO = '#c4cee0';
const GRADE = 'rgba(255,255,255,0.05)';

const sim = valor => (valor ? 'Sim' : 'Não');
const decimal = valor => Number(valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const numero = (valor, sufixo = '') => (valor != null && Number.isFinite(Number(valor)) ? `${decimal(valor)}${sufixo}` : '—');
const reais = valor => Number(valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

// lightweight-charts entrega o tempo como 'AAAA-MM-DD' ou BusinessDay { year, month, day }.
const dataIso = tempo => {
  if (typeof tempo === 'string') return tempo;
  if (tempo && tempo.year) return `${tempo.year}-${String(tempo.month).padStart(2, '0')}-${String(tempo.day).padStart(2, '0')}`;
  return Number.isFinite(tempo) ? new Date(tempo * 1000).toISOString().slice(0, 10) : '';
};
export const dataBr = iso => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : '—');
const marcaTempo = (tempo, tipo) => {
  const iso = dataIso(tempo);
  if (tipo === 0) return iso.slice(0, 4); // ano
  if (tipo === 1) return `${MESES[Number(iso.slice(5, 7)) - 1]}/${iso.slice(2, 4)}`; // mês
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
};
const temOhlc = s => [s.open, s.high, s.low, s.close].every(v => v != null && Number.isFinite(Number(v)));

function opcoesBase(altura, mostrarTempo) {
  return {
    autoSize: true,
    height: altura,
    layout: { background: { color: FUNDO }, textColor: TEXTO, fontSize: 12, attributionLogo: true },
    grid: { vertLines: { color: GRADE }, horzLines: { color: GRADE } },
    crosshair: { mode: CrosshairMode.Normal },
    rightPriceScale: { borderColor: GRADE, minimumWidth: 84 },
    timeScale: { borderColor: GRADE, visible: mostrarTempo, tickMarkFormatter: marcaTempo, rightOffset: 3 },
    localization: { locale: 'pt-BR', timeFormatter: tempo => dataBr(dataIso(tempo)) },
  };
}

// Faixa vertical da semana de referência: histograma 0..1 numa escala própria, invisível, ocupando a altura toda.
function adicionarFaixa(chart, semanas, referencia) {
  const faixa = chart.addHistogramSeries({
    priceScaleId: 'faixa', lastValueVisible: false, priceLineVisible: false,
    autoscaleInfoProvider: () => ({ priceRange: { minValue: 0, maxValue: 1 } }),
  });
  chart.priceScale('faixa').applyOptions({ visible: false, scaleMargins: { top: 0, bottom: 0 } });
  faixa.setData(semanas.map(s => (s.data === referencia ? { time: s.data, value: 1, color: 'rgba(245,197,66,0.16)' } : { time: s.data })));
}

function GraficoSemanal({ grafico }) {
  const precoRef = useRef(null);
  const rsiRef = useRef(null);
  const [dica, setDica] = useState(null);

  useEffect(() => {
    if (!precoRef.current || !rsiRef.current) return undefined;
    const { semanas, semanaReferencia: ref, semanasAbaixoCorte: abaixo = [], corte, limiarSaida, marcadores = [], posicao } = grafico;
    const porData = new Map(semanas.map(s => [s.data, s]));
    const outras = new Set(abaixo);

    const precos = createChart(precoRef.current, {
      ...opcoesBase(360, false),
      localization: { ...opcoesBase(360, false).localization, priceFormatter: reais },
    });
    const rsi = createChart(rsiRef.current, {
      ...opcoesBase(170, true),
      rightPriceScale: { ...opcoesBase(170, true).rightPriceScale, scaleMargins: { top: 0, bottom: 0 } },
      localization: { ...opcoesBase(170, true).localization, priceFormatter: decimal },
    });

    adicionarFaixa(precos, semanas, ref);
    const velas = precos.addCandlestickSeries({
      upColor: ALTA, downColor: BAIXA, borderUpColor: ALTA, borderDownColor: BAIXA, wickUpColor: ALTA, wickDownColor: BAIXA,
    });
    velas.setData(semanas.map(s => (temOhlc(s)
      ? { time: s.data, open: Number(s.open), high: Number(s.high), low: Number(s.low), close: Number(s.close) }
      : { time: s.data })));
    // FAT-47: entradas/saídas registradas no histórico de sinais (FAT-28), na semana do registro (já filtradas pelo backend).
    const registrados = marcadores.filter(m => porData.has(m.data)).map(m => (m.tipo === 'entrada'
      ? { time: m.data, position: 'belowBar', color: ENTRADA, shape: 'arrowUp', text: 'Entrada' }
      : { time: m.data, position: 'aboveBar', color: SAIDA, shape: 'square', text: 'Saída' }));
    velas.setMarkers([...semanas
      .filter(s => s.data === ref || outras.has(s.data))
      .map(s => (s.data === ref
        ? { time: s.data, position: 'aboveBar', color: DESTAQUE, shape: 'arrowDown', text: 'Sinal' }
        : { time: s.data, position: 'belowBar', color: DISCRETO, shape: 'circle', size: 0.6 })), ...registrados]
      .sort((a, b) => (a.time < b.time ? -1 : a.time > b.time ? 1 : 0)));
    if (posicao) { // posição aberta segundo os registros: entrada e referência de perda (só visual)
      velas.createPriceLine({ price: posicao.precoEntrada, color: ENTRADA, lineWidth: 1, lineStyle: LineStyle.Solid, axisLabelVisible: true, title: 'Entrada' });
      velas.createPriceLine({
        price: posicao.referenciaPerda, color: BAIXA, lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: true, title: `Perda −${grafico.quedaMaximaPct}%`,
      });
    }

    adicionarFaixa(rsi, semanas, ref);
    const linhaRsi = rsi.addBaselineSeries({
      baseValue: { type: 'price', price: corte },
      topLineColor: '#7fa8ff', topFillColor1: 'rgba(0,0,0,0)', topFillColor2: 'rgba(0,0,0,0)',
      bottomLineColor: BAIXA, bottomFillColor1: 'rgba(239,83,80,0.35)', bottomFillColor2: 'rgba(239,83,80,0.10)',
      lineWidth: 2, priceLineVisible: false,
      autoscaleInfoProvider: () => ({ priceRange: { ...ESCALA_RSI }, margins: { above: 0, below: 0 } }),
    });
    linhaRsi.setData(semanas.map(s => (s.rsi2 != null ? { time: s.data, value: s.rsi2 } : { time: s.data })));
    linhaRsi.createPriceLine({ price: corte, color: BAIXA, lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: true, title: `entrada < ${corte}` });
    if (limiarSaida != null) {
      linhaRsi.createPriceLine({ price: limiarSaida, color: SAIDA, lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: true, title: `saída > ${limiarSaida}` });
    }
    // inBar: com a escala travada em 0–100, marcador abaixo da barra sairia do painel.
    linhaRsi.setMarkers(semanas
      .filter(s => s.data === ref || outras.has(s.data))
      .map(s => (s.data === ref
        ? { time: s.data, position: 'inBar', color: DESTAQUE, shape: 'circle', text: decimal(s.rsi2) }
        : { time: s.data, position: 'inBar', color: DISCRETO, shape: 'circle', size: 0.6 })));
    precos.timeScale().fitContent();
    rsi.timeScale().fitContent();

    // Mesmo zoom/arraste nos dois painéis (mesmos pontos de tempo ⇒ mesmo intervalo lógico).
    let sincronizando = false;
    const espelhar = destino => faixaLogica => {
      if (!faixaLogica || sincronizando) return;
      sincronizando = true;
      destino.timeScale().setVisibleLogicalRange(faixaLogica);
      sincronizando = false;
    };
    const dePrecos = espelhar(rsi);
    const deRsi = espelhar(precos);
    precos.timeScale().subscribeVisibleLogicalRangeChange(dePrecos);
    rsi.timeScale().subscribeVisibleLogicalRangeChange(deRsi);

    const aoMover = (outro, serieOutro, valorOutro) => param => {
      const semana = param && param.time != null ? porData.get(dataIso(param.time)) : null;
      setDica(semana || null);
      if (!semana) { outro.clearCrosshairPosition(); return; }
      const valor = valorOutro(semana);
      if (valor != null) outro.setCrosshairPosition(valor, semana.data, serieOutro);
      else outro.clearCrosshairPosition();
    };
    const moverPrecos = aoMover(rsi, linhaRsi, s => s.rsi2);
    const moverRsi = aoMover(precos, velas, s => (temOhlc(s) ? Number(s.close) : null));
    precos.subscribeCrosshairMove(moverPrecos);
    rsi.subscribeCrosshairMove(moverRsi);

    return () => {
      precos.timeScale().unsubscribeVisibleLogicalRangeChange(dePrecos);
      rsi.timeScale().unsubscribeVisibleLogicalRangeChange(deRsi);
      precos.unsubscribeCrosshairMove(moverPrecos);
      rsi.unsubscribeCrosshairMove(moverRsi);
      precos.remove();
      rsi.remove();
    };
  }, [grafico]);

  return (
    <div className="agp-graficos">
      <div className="agp-dica" data-testid="alpha-grafico-dica" aria-live="polite">
        {dica ? (
          <>
            <strong>Semana de {dataBr(dica.semana)} a {dataBr(dica.data)}</strong>
            {temOhlc(dica)
              ? <span> · A {reais(dica.open)} · M {reais(dica.high)} · m {reais(dica.low)} · F {reais(dica.close)}</span>
              : <span> · sem OHLC completo</span>}
            <span> · RSI(2) {numero(dica.rsi2)}</span>
          </>
        ) : <span>Passe o mouse (ou toque) num candle para ver abertura, máxima, mínima e fechamento. Role para zoom, arraste para mover.</span>}
      </div>
      <div ref={precoRef} className="agp-precos" data-testid="alpha-grafico-precos" />
      {grafico.posicao && (
        <p className="agp-nota-posicao" data-testid="alpha-grafico-posicao">
          Em posição: entrada de referência {reais(grafico.posicao.precoEntrada)} em {dataBr(grafico.posicao.dataEntrada)} · referência de
          perda {reais(grafico.posicao.referenciaPerda)} (entrada −{grafico.quedaMaximaPct}%), {NOTA_POSICAO}.
        </p>
      )}
      <div className="agp-rotulo" data-testid="alpha-grafico-rotulo-rsi">
        RSI(2) semanal (0–100) · <span className="agp-legenda-rsi" data-testid="alpha-grafico-legenda-rsi">
          {`entrada < ${grafico.corte}${grafico.limiarSaida != null ? ` · saída > ${grafico.limiarSaida}` : ''}`}
        </span>
      </div>
      <div ref={rsiRef} className="agp-rsi" data-testid="alpha-grafico-rsi" />
    </div>
  );
}

// FAT-47: os números vêm da mesma linha do scanner (backend `explicacao`); ausente = "—", nada inventado.
export function PorQueApareceu({ explicacao, corte, parametros }) {
  const e = explicacao || {};
  const simNao = valor => (valor == null ? '—' : sim(valor));
  const janela = e.janelaMercadoPregoes ?? parametros?.janelaMercadoPregoes;
  const rsiAbaixo = e.rsiSemanal != null && corte != null && Number(e.rsiSemanal) < corte;
  return (
    <section className="agp-porque" data-testid="alpha-grafico-porque" aria-labelledby="agp-porque-titulo">
      <h4 id="agp-porque-titulo">Por que apareceu?</h4>
      <dl>
        <div>
          <dt>Mercado favorável?</dt>
          <dd data-testid="alpha-porque-mercado">
            {simNao(e.mercadoFavoravel)} · IBOV em {janela ?? '—'} pregões: {percentualAlpha(e.retornoIbovPct)}
          </dd>
        </div>
        <div><dt>Liquidez elegível?</dt><dd data-testid="alpha-porque-liquidez">{simNao(e.liquidezElegivel)}</dd></div>
        <div>
          <dt>RSI semanal</dt>
          <dd data-testid="alpha-porque-rsi">{numero(e.rsiSemanal)}{rsiAbaixo ? ` (abaixo de ${corte})` : ''}</dd>
        </div>
        <div>
          <dt>Ranking de volatilidade</dt>
          <dd data-testid="alpha-porque-ranking">
            {e.ranking != null && e.totalRanking ? `${e.ranking} de ${e.totalRanking} candidatas (menor volatilidade = 1)` : '—'}
          </dd>
        </div>
      </dl>
    </section>
  );
}

export default function AlphaGraficoPainel({ linha, parametros, onFechar }) {
  const [estado, setEstado] = useState({ carregando: true, grafico: null, erro: null });
  const fecharRef = useRef(null);
  const ativo = linha.ativo;

  useEffect(() => {
    let vivo = true; // resposta tardia (troca de candidata/fechou) é descartada
    setEstado({ carregando: true, grafico: null, erro: null });
    getAlphaGrafico(ativo)
      .then(grafico => { if (vivo) setEstado({ carregando: false, grafico, erro: null }); })
      .catch(err => {
        if (vivo) setEstado({ carregando: false, grafico: null, erro: err?.response?.data?.error || err.message || 'Falha ao carregar o gráfico.' });
      });
    return () => { vivo = false; };
  }, [ativo]);

  const fecharAtual = useRef(onFechar);
  fecharAtual.current = onFechar;
  useEffect(() => {
    const anterior = document.activeElement;
    fecharRef.current?.focus({ preventScroll: true });
    const aoTeclar = e => { if (e.key === 'Escape') fecharAtual.current(); };
    document.addEventListener('keydown', aoTeclar);
    return () => {
      document.removeEventListener('keydown', aoTeclar);
      if (anterior && typeof anterior.focus === 'function') anterior.focus({ preventScroll: true });
    };
  }, []);

  const { carregando, grafico, erro } = estado;
  const vazio = !carregando && !erro && (!grafico || grafico.semHistorico || !grafico.semanas?.length);
  const corte = grafico?.corte ?? parametros?.limiteRsiSemanal ?? 20;
  const abaixo = grafico?.rsiReferencia != null && grafico.rsiReferencia < corte;
  const semanaRef = grafico?.semanas?.find(s => s.data === grafico.semanaReferencia);

  return (
    <div className="agp-fundo" data-testid="alpha-grafico-fundo" onMouseDown={e => { if (e.target === e.currentTarget) onFechar(); }}>
      <div className="agp-painel" role="dialog" aria-modal="true" aria-labelledby="agp-titulo" data-testid="alpha-grafico-painel">
        <header className="agp-cabecalho">
          <div>
            <h3 id="agp-titulo">{ativo} · candles semanais e RSI(2) semanal</h3>
            <dl className="agp-resumo" data-testid="alpha-grafico-resumo">
              <div><dt>Ranking</dt><dd>{linha.ranking ?? '—'}</dd></div>
              <div><dt>Mercado favorável?</dt><dd>{sim(linha.mercadoFavoravel)}</dd></div>
              <div><dt>Liquidez elegível?</dt><dd>{sim(linha.liquidezElegivel)}</dd></div>
              <div><dt>RSI semanal</dt><dd>{numero(linha.rsiSemanal)}</dd></div>
              <div><dt>Volatilidade 100 dias</dt><dd>{numero(linha.volatilidade, '%')}</dd></div>
              <div><dt>Situação</dt><dd>{linha.estado || linha.situacao}</dd></div>
            </dl>
            <p className="agp-motivo" data-testid="alpha-grafico-motivo">{motivoAlpha(linha, parametros)}</p>
          </div>
          <button type="button" ref={fecharRef} className="agp-fechar" aria-label="Fechar gráfico" data-testid="alpha-grafico-fechar" onClick={onFechar}>×</button>
        </header>
        <p className="agp-aviso">Candidata ainda depende de capital e posições existentes. O FatCat não executa ordens.</p>

        {carregando && <p className="agp-estado" data-testid="alpha-grafico-carregando">Carregando gráfico…</p>}
        {erro && <p className="agp-estado" role="alert" data-testid="alpha-grafico-erro">{erro}</p>}
        {vazio && <p className="agp-estado" data-testid="alpha-grafico-vazio">{ativo}: sem histórico suficiente para o gráfico semanal.</p>}
        {!carregando && !erro && !vazio && (
          <>
            <p className="agp-legenda" data-testid="alpha-grafico-legenda">
              <span className="agp-seta" aria-hidden="true">▼</span>
              {abaixo
                ? `RSI(2) semanal ${decimal(grafico.rsiReferencia)} < ${corte} nesta semana`
                : `RSI(2) semanal ${decimal(grafico.rsiReferencia)} nesta semana`}
              {semanaRef && ` (${dataBr(semanaRef.semana)} a ${dataBr(semanaRef.data)})`}
              {grafico.semanasAbaixoCorte?.length > 0 && (
                <span className="agp-outras"> · ● outras {grafico.semanasAbaixoCorte.length} semana(s) com RSI &lt; {corte} no período</span>
              )}
              {grafico.marcadores?.length > 0 && (
                <span className="agp-outras" data-testid="alpha-grafico-legenda-registros"> · ▲ Entrada · ■ Saída registradas no histórico de sinais</span>
              )}
            </p>
            <GraficoSemanal grafico={grafico} />
            <p className="agp-atribuicao">
              Gráfico: <a href="https://www.tradingview.com/" target="_blank" rel="noopener noreferrer">TradingView Lightweight Charts™</a>
              {' '}· {grafico.semanas.length} semanas encerradas até {dataBr(grafico.semanaReferencia)} · preços ajustados
            </p>
          </>
        )}
        {!carregando && !erro && grafico && <PorQueApareceu explicacao={grafico.explicacao} corte={corte} parametros={parametros} />}
      </div>
    </div>
  );
}
